"use client";

import { useState, useRef } from "react";
import { useDropzone } from "react-dropzone";
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Modal,
  ModalTrigger,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
} from "@/components/modal";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import { env } from "@/env";
import { Upload, ZoomIn, ZoomOut } from "lucide-react";
import "react-image-crop/dist/ReactCrop.css";

const MAX_FILE_SIZE = 1024 * 1024 * 2; // 2MB
const MIN_DIMENSIONS = 128; // 128x128 pixels minimum
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.1;

function ChangeAvatarModal() {
  const { accessToken, updateUserDetails, user } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [imgSrc, setImgSrc] = useState("");
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const imgRef = useRef<HTMLImageElement>(null);

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;

    const size = Math.min(width, height);
    const x = (width - size) / 2;
    const y = (height - size) / 2;

    const initialCrop: Crop = {
      unit: "%",
      x: (x / width) * 100,
      y: (y / height) * 100,
      width: (size / width) * 100,
      height: (size / height) * 100,
    };

    setCrop(initialCrop);
  }

  const handleZoomIn = () => {
    if (imgRef.current && crop) {
      const newScale = Math.min(scale + ZOOM_STEP, MAX_ZOOM);
      setScale(newScale);

      const img = imgRef.current;
      const scaledWidth = img.width * newScale;
      const scaledHeight = img.height * newScale;

      setCrop((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          x: (prev.x * scaledWidth) / img.width,
          y: (prev.y * scaledHeight) / img.height,
        };
      });
    }
  };

  const handleZoomOut = () => {
    if (imgRef.current && crop) {
      const newScale = Math.max(scale - ZOOM_STEP, MIN_ZOOM);
      setScale(newScale);

      const img = imgRef.current;
      const scaledWidth = img.width * newScale;
      const scaledHeight = img.height * newScale;

      setCrop((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          x: (prev.x * scaledWidth) / img.width,
          y: (prev.y * scaledHeight) / img.height,
        };
      });
    }
  };

  function getCroppedImg(
    image: HTMLImageElement,
    pixelCrop: PixelCrop,
  ): Promise<string> {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("No 2d context");
    }

    canvas.width = MIN_DIMENSIONS;
    canvas.height = MIN_DIMENSIONS;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const sourceX = (pixelCrop.x / scale) * scaleX;
    const sourceY = (pixelCrop.y / scale) * scaleY;
    const sourceWidth = (pixelCrop.width / scale) * scaleX;
    const sourceHeight = (pixelCrop.height / scale) * scaleY;

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      MIN_DIMENSIONS,
      MIN_DIMENSIONS,
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => {
            resolve(reader.result as string);
          };
        },
        "image/jpeg",
        1,
      );
    });
  }

  const validateImageDimensions = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        resolve(img.width >= MIN_DIMENSIONS && img.height >= MIN_DIMENSIONS);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const onDrop = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];

    if (!file) {
      return toast({
        title: "Error",
        description: "Please upload a valid image",
        variant: "destructive",
      });
    }

    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: "Error",
        description: `${file.name} is too large. Max size is 2MB`,
        variant: "destructive",
      });
      return;
    }

    const isValidDimensions = await validateImageDimensions(file);
    if (!isValidDimensions) {
      toast({
        title: "Error",
        description: "Image must be at least 128x128 pixels",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.addEventListener("load", () => {
      setImgSrc(reader.result?.toString() || "");
    });
    reader.readAsDataURL(file);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/*": [".jpg", ".jpeg", ".png", ".gif", ".webp"],
    },
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE,
    onDrop,
  });

  const handleSave = async () => {
    if (!imgRef.current || !completedCrop) {
      return;
    }

    try {
      const croppedImage = await getCroppedImg(imgRef.current, completedCrop);
      await changeAvatar(croppedImage);
      setOpen(false);
      setImgSrc("");
      setCrop(undefined);
      setCompletedCrop(undefined);
    } catch (e) {
      toast({
        title: "Error",
        description: "Failed to process image",
        variant: "destructive",
      });
    }
  };

  const handleCropChange = (pixelCrop: PixelCrop, percentCrop: Crop) => {
    setCrop(percentCrop);
    setCompletedCrop(pixelCrop);
  };

  async function changeAvatar(image: string) {
    try {
      const response = await fetch(
        `${env.NEXT_PUBLIC_API_URL}/user/change-avatar`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ base64: image }),
        },
      );

      if (response.ok) {
        updateUserDetails({ avatar: `${user?.username}/avatar.webp` });
        toast({
          title: "Success",
          description: "Avatar changed successfully",
        });
      } else {
        throw new Error("Failed to update avatar");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update avatar",
        variant: "destructive",
      });
    }
  }

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalTrigger className={buttonVariants({ variant: "secondary" })}>
        Change Avatar
      </ModalTrigger>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Change Avatar</ModalTitle>
        </ModalHeader>
        <ModalBody className="overflow-hidden p-0">
          {!imgSrc ? (
            <div
              {...getRootProps({
                className:
                  "hover:bg-white/5 transition hover:cursor-pointer gap-2 flex items-center justify-center flex-col border-dashed border border-input p-8 rounded-md",
              })}
            >
              <input {...getInputProps()} />
              <div className="rounded-full bg-white/5 p-4">
                <Upload
                  className="h-4 w-4 text-muted-foreground"
                  aria-hidden="true"
                />
              </div>
              <span className="flex flex-col gap-px text-center">
                <p>Drag n drop an image here, or click to select one</p>
                <span className="text-xs text-muted-foreground">
                  Image must be at least 128x128 pixels and max 2MB
                </span>
              </span>
            </div>
          ) : (
            <div className="p-4">
              <div className="mb-4 flex justify-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleZoomOut}
                  disabled={scale <= MIN_ZOOM}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleZoomIn}
                  disabled={scale >= MAX_ZOOM}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
              <ReactCrop
                crop={crop}
                onChange={handleCropChange}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                circularCrop
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={imgSrc}
                  onLoad={onImageLoad}
                  className="max-h-[400px] w-auto"
                  style={{ transform: `scale(${scale})` }}
                />
              </ReactCrop>
            </div>
          )}
          {imgSrc && (
            <div className="flex justify-end gap-2 p-6 pt-0">
              <Button
                variant="ghost"
                onClick={() => {
                  setImgSrc("");
                  setCrop(undefined);
                  setCompletedCrop(undefined);
                  setScale(1);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSave}>Save</Button>
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default ChangeAvatarModal;
